import React, { useState, useEffect } from 'react'
import { Save, AlertCircle, CheckCircle, Globe, Mail, Users, Sparkles } from 'lucide-react'

interface PlatformSettings {
  dailyAIGenerationLimit: number
  platformName: string
  platformEmail: string
  maxTenantsAllowed: number
  lastUpdated: string
  lastUpdatedBy: string
}

interface PlatformAdminProps {
  onSettingsUpdate?: () => void
}

const PlatformAdmin: React.FC<PlatformAdminProps> = ({ onSettingsUpdate }) => {
  const [settings, setSettings] = useState<PlatformSettings | null>(null)
  const [editedSettings, setEditedSettings] = useState<Partial<PlatformSettings>>({})
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    try {
      setLoading(true)
      const authToken = localStorage.getItem('auth-token')
      const response = await fetch('/api/admin/platform-settings', {
        headers: {
          'Authorization': `Bearer ${authToken || ''}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch platform settings')
      }

      const data = await response.json()
      setSettings(data.settings)
      setIsPlatformAdmin(data.isPlatformAdmin)
      setEditedSettings(data.settings)
    } catch (error) {
      console.error('Error fetching platform settings:', error)
      setMessage({ type: 'error', text: 'Failed to load platform settings' })
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!isPlatformAdmin) {
      setMessage({ type: 'error', text: 'Only platform administrators can modify these settings' })
      return
    }

    try {
      setSaving(true)
      setMessage(null)

      const authToken = localStorage.getItem('auth-token')
      const response = await fetch('/api/admin/platform-settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken || ''}`
        },
        body: JSON.stringify({
          updates: editedSettings
        })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to update settings')
      }

      const data = await response.json()
      setSettings(data.settings)
      setEditedSettings(data.settings)
      setMessage({ type: 'success', text: 'Platform settings updated successfully!' })

      if (onSettingsUpdate) {
        onSettingsUpdate()
      }
    } catch (error) {
      console.error('Error saving platform settings:', error)
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to save settings'
      })
    } finally {
      setSaving(false)
    }
  }

  const hasChanges = settings && JSON.stringify(settings) !== JSON.stringify(editedSettings)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!settings) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Failed to load platform settings</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Platform Administration</h1>
        <p className="mt-1 text-sm text-gray-500">
          Configure global platform settings that apply to all tenants
        </p>
      </div>

      {/* Access Warning for Non-Admins */}
      {!isPlatformAdmin && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">View Only</h3>
              <p className="mt-1 text-sm text-yellow-700">
                You can view these settings, but only platform administrators can modify them.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Success/Error Messages */}
      {message && (
        <div className={`border rounded-lg p-4 ${
          message.type === 'success'
            ? 'bg-green-50 border-green-200'
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-start">
            {message.type === 'success' ? (
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3" />
            ) : (
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3" />
            )}
            <p className={`text-sm ${
              message.type === 'success' ? 'text-green-800' : 'text-red-800'
            }`}>
              {message.text}
            </p>
          </div>
        </div>
      )}

      {/* Settings Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 space-y-6">
          {/* AI Generation Settings */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Sparkles className="h-5 w-5 mr-2 text-purple-600" />
              AI Generation Settings
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Daily AI Generation Limit (per tenant)
                </label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={editedSettings.dailyAIGenerationLimit || 0}
                  onChange={(e) => setEditedSettings({
                    ...editedSettings,
                    dailyAIGenerationLimit: parseInt(e.target.value)
                  })}
                  disabled={!isPlatformAdmin}
                  className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p className="mt-2 text-sm text-gray-500">
                  Maximum AI generations per day when tenants use the global API key. Tenants with their own API key have unlimited generations.
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Globe className="h-5 w-5 mr-2 text-blue-600" />
              Platform Information
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Platform Name
                </label>
                <input
                  type="text"
                  value={editedSettings.platformName || ''}
                  onChange={(e) => setEditedSettings({
                    ...editedSettings,
                    platformName: e.target.value
                  })}
                  disabled={!isPlatformAdmin}
                  className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="TrustCenter Platform"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Mail className="h-4 w-4 mr-2" />
                  Platform Email
                </label>
                <input
                  type="email"
                  value={editedSettings.platformEmail || ''}
                  onChange={(e) => setEditedSettings({
                    ...editedSettings,
                    platformEmail: e.target.value
                  })}
                  disabled={!isPlatformAdmin}
                  className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="admin@trustcenter.page"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Users className="h-5 w-5 mr-2 text-green-600" />
              Tenant Limits
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Tenants Allowed
                </label>
                <input
                  type="number"
                  min="1"
                  max="10000"
                  value={editedSettings.maxTenantsAllowed || 0}
                  onChange={(e) => setEditedSettings({
                    ...editedSettings,
                    maxTenantsAllowed: parseInt(e.target.value)
                  })}
                  disabled={!isPlatformAdmin}
                  className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p className="mt-2 text-sm text-gray-500">
                  Maximum number of tenants that can be created on this platform.
                </p>
              </div>
            </div>
          </div>

          {/* Last Updated Info */}
          {settings.lastUpdated && (
            <div className="border-t border-gray-200 pt-4">
              <p className="text-xs text-gray-500">
                Last updated: {new Date(settings.lastUpdated).toLocaleString()} by {settings.lastUpdatedBy}
              </p>
            </div>
          )}
        </div>

        {/* Save Button */}
        {isPlatformAdmin && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default PlatformAdmin
